import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { PlantefeveLogoComponent } from '@plantefeve/common/components/plantefeve-logo/plantefeve-logo.component';
import { AuthenticationService } from '@plantefeve/common/services/authentication.service';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { StyleClassModule } from 'primeng/styleclass';
import { TooltipModule } from 'primeng/tooltip';
import { Observable } from 'rxjs';
import { LanguageOption } from '../../models/layout.models';
import { LayoutService } from '../../services/layout.service';
import { MenuItemComponent } from './../menu-item/menu-item.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'igx-topbar',
  imports: [
    PlantefeveLogoComponent,
    StyleClassModule,
    ButtonModule,
    MenuItemComponent,
    MenuItemComponent,
    CommonModule,
    TooltipModule,
    MenuModule,
    TranslatePipe,
  ],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss',
})
export class TopbarComponent {
  private readonly _layoutService = inject(LayoutService);
  private readonly _router = inject(Router);
  private readonly _authService = inject(AuthenticationService);
  private readonly _translaterService = inject(TranslateService);
  public menu = input.required<MenuItem[]>();
  public hideSideBar = input<boolean>(false);
  /** Languages the user can switch between in the profile menu; the backoffice passes none and shows none. */
  public languages = input<LanguageOption[]>([]);
  public currentLanguage = input<string>();
  public languageChange = output<string>();
  public isLoggedIn$: Observable<boolean> = this._authService.isLoggedIn();

  public menuItems = computed<MenuItem[]>(() => {
    const languages = this.languages();
    return [
      {
        label: this._translaterService.instant('Mijn profiel'),
        icon: 'pi pi-user',
        command: () => this._router.navigate(['/users', 'details']),
      },
      ...(languages.length > 0
        ? [
            { separator: true },
            ...languages.map((language) => ({
              label: language.label,
              // pi-fw keeps the labels aligned next to the check mark of the current language. The current one is
              // also disabled: screen readers then announce it as unavailable, and there is nothing to switch to.
              icon: language.code === this.currentLanguage() ? 'pi pi-fw pi-check' : 'pi pi-fw',
              disabled: language.code === this.currentLanguage(),
              command: () => this.languageChange.emit(language.code),
            })),
            { separator: true },
          ]
        : []),
      {
        label: this._translaterService.instant('Uitloggen'),
        icon: 'pi pi-sign-out',
        command: () => this.logout(),
      },
    ];
  });

  constructor() {
    effect(() => {
      if (this.hideSideBar()) {
        this._layoutService.layoutConfig.update((config) => ({
          ...config,
          menuMode: 'overlay',
        }));
      }
    });
  }

  public onMenuToggle(): void {
    this._layoutService.onMenuToggle();
  }

  public showOrders(): void {
    this._router.navigate(['/orders']);
  }

  public logout(): void {
    this._authService.logout();
  }
}
